import { useState, useCallback, useContext } from 'react';
import * as bip39 from 'bip39';
import { DirectSecp256k1HdWallet } from '@cosmjs/proto-signing';
import { SigningStargateClient, GasPrice } from '@cosmjs/stargate';

const PREFIX    = 'amitis';
const RPC       = 'https://rpc.amitis.network';
const REST      = 'https://rest.amitis.network';
const DENOM     = 'uamts';
const GAS_PRICE = GasPrice.fromString('0.025uamts');

export function useNativeWallet() {
  const [wallet,   setWallet]   = useState(null);
  const [address,  setAddress]  = useState(null);
  const [mnemonic, setMnemonic] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  const fetchBalance = useCallback(async (addr) => {
    try {
      const res  = await fetch(`${REST}/cosmos/bank/v1beta1/balances/${addr}`);
      const data = await res.json();
      const u    = (data.balances || []).find(b => b.denom === DENOM);
      return u ? parseInt(u.amount) / 1_000_000 : 0;
    } catch { return 0; }
  }, []);

  const generate = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const mn = bip39.generateMnemonic(256);
      const w  = await DirectSecp256k1HdWallet.fromMnemonic(mn, { prefix: PREFIX });
      const [acct] = await w.getAccounts();
      setMnemonic(mn);
      setWallet(w);
      setAddress(acct.address);
      return { mnemonic: mn, address: acct.address };
    } catch (e) { setError(e.message); throw e; }
    finally { setLoading(false); }
  }, []);

  const importWallet = useCallback(async (mn) => {
    setLoading(true); setError(null);
    try {
      const clean = mn.trim().toLowerCase().replace(/\s+/g, ' ');
      if (!bip39.validateMnemonic(clean)) throw new Error('Invalid seed phrase. Check your words and try again.');
      const w = await DirectSecp256k1HdWallet.fromMnemonic(clean, { prefix: PREFIX });
      const [acct] = await w.getAccounts();
      setWallet(w);
      setAddress(acct.address);
      setMnemonic(null);
      return acct.address;
    } catch (e) { setError(e.message); throw e; }
    finally { setLoading(false); }
  }, []);

  const confirmBackup = useCallback(() => setMnemonic(null), []);

  const disconnect = useCallback(() => {
    setWallet(null); setAddress(null); setMnemonic(null); setError(null);
  }, []);

  const send = useCallback(async (toAddress, amountAmts, memo = '') => {
    if (!wallet) throw new Error('No wallet connected');
    setLoading(true); setError(null);
    try {
      const client  = await SigningStargateClient.connectWithSigner(RPC, wallet, { gasPrice: GAS_PRICE });
      const uamts   = Math.floor(parseFloat(amountAmts) * 1_000_000);
      // Amitis monetary module requires 0.3% fee on MsgSend amount (minimum 30000 uamts)
      const feeAmt  = Math.max(Math.ceil(uamts * 0.003), 30000).toString();
      const fee     = { amount: [{ denom: DENOM, amount: feeAmt }], gas: '200000' };
      return await client.sendTokens(address, toAddress, [{ denom: DENOM, amount: uamts.toString() }], fee, memo);
    } catch (e) { setError(e.message); throw e; }
    finally { setLoading(false); }
  }, [wallet, address]);

  return {
    wallet, address, mnemonic, loading, error,
    generate, importWallet, confirmBackup, disconnect, send, fetchBalance,
    isConnected: !!address,
  };
}
